import { useState } from 'react'
import {Plus, AlertTriangle} from 'lucide-react'

type Item = { q: string; a: string; tag: string }

const ITEMS: Item[] = [
  {
    q: 'Does Low Power Mode damage device batteries?',
    a: 'No. Low Power Mode is a software state — it throttles CPU frequency, dims the screen, and pauses background fetches. None of these touch the lithium-ion chemistry. In fact, by reducing thermal load and the depth of daily discharge, regular use can modestly extend overall pack lifespan over multiple years.',
    tag: 'Safety · Myth-buster',
  },
  {
    q: 'How to turn off cellular data intelligently to save power?',
    a: 'Don’t blanket-disable cellular. Instead: (1) toggle airplane mode in known dead zones — radios at full transmit power are battery hogs; (2) per-app cellular restriction in Settings → Cellular for chat and social apps; (3) prefer 5G Auto over 5G On — Auto uses LTE when 5G isn’t materially faster.',
    tag: 'Connectivity',
  },
  {
    q: 'Does dark mode truly preserve power on classic LCD displays?',
    a: 'Largely no. LCDs use a constant backlight regardless of pixel color, so dark mode saves only a token amount on LCD panels. The 15%+ gains apply specifically to OLED and AMOLED screens, where black pixels are physically switched off. Check your device’s display spec before relying on dark mode for endurance.',
    tag: 'Display Science',
  },
  {
    q: 'Should I keep my phone plugged in overnight?',
    a: 'Modern phones stop charging at 100% and trickle to maintain — so it’s safe — but heat is the actual enemy. Use Optimized Battery Charging (iOS) or Adaptive Charging (Android) to cap at 80% until just before your alarm. This dramatically reduces calendar-aging of the cell.',
    tag: 'Charging',
  },
  {
    q: 'Is closing background apps actually helpful?',
    a: 'Counter-intuitively, no — and it can hurt. iOS and Android suspend background apps in milliseconds; force-quitting them means the OS has to cold-launch the app next time, costing more energy than it saved. Only force-quit apps that are visibly misbehaving.',
    tag: 'Optimization',
  },
]

function Faq() {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faq" className="relative py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left rail */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.24em] text-amber-700 mb-4">§ 03 · Direct Answers</div>
            <h2 className="font-display text-slate-900 text-[clamp(2rem,4.5vw,3.75rem)] leading-[1] font-medium">
              Battery questions, <br />
              <span className="italic font-light">answered without filler.</span>
            </h2>
            <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.7] max-w-md">
              Short, accurate, and source-grade. Each answer is calibrated against manufacturer documentation and independent benchmarks.
            </p>

            {/* Energy warning card */}
            <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-50/60 p-5 max-w-md">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 text-amber-700" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-display text-[15px] font-medium text-amber-950">Heat is the real enemy.</div>
                  <p className="text-amber-900/80 text-[13px] mt-1 leading-relaxed">
                    A battery aged at 35°C loses capacity ~2× faster than one at 20°C. Cooling matters more than charging habits.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Accordion */}
          <div className="lg:col-span-7 space-y-3">
            {ITEMS.map((it, i) => {
              const isOpen = open === i
              return (
                <div
                  key={i}
                  className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                    isOpen
                      ? 'bg-bone-2 border-slate-900/15'
                      : 'bg-bone border-slate-900/[0.07] hover:border-slate-900/15'
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full text-left px-6 py-5 flex items-start gap-5"
                  >
                    <div className="font-mono text-[11px] text-slate-500 mt-1 tabular-nums">Q.{String(i + 1).padStart(2, '0')}</div>
                    <div className="flex-1">
                      <div className="font-display text-[18px] lg:text-[20px] font-medium text-slate-900 leading-snug">{it.q}</div>
                      <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-amber-700 mt-2">{it.tag}</div>
                    </div>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                      isOpen ? 'bg-slate-900 border-slate-900 rotate-45' : 'border-slate-900/20 group-hover:border-slate-900/40'
                    }`}>
                      <Plus className={`w-4 h-4 ${isOpen ? 'text-amber-400' : 'text-slate-700'}`} strokeWidth={2} />
                    </div>
                  </button>
                  <div
                    className="grid transition-all duration-500 ease-out"
                    style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 pt-0 ml-[60px] max-w-2xl">
                        <p className="text-slate-700 text-[15px] leading-[1.7]">{it.a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Faq
